import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { radioStations, categoriesWithIcons, type RadioStation } from '@/mocks/radios';
import { useAudioPlayer } from '@/context/AudioContext';
import { useSwipeGesture } from '@/hooks/useSwipeGesture';

export default function DialTab() {
  const { play, currentStation, isPlaying } = useAudioPlayer();
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const filtered = radioStations.filter((s) => {
    const matchSearch =
      !search ||
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.frequency.includes(search) ||
      s.city.toLowerCase().includes(search.toLowerCase());
    const matchCat = !activeCategory || s.category === activeCategory;
    return matchSearch && matchCat;
  });

  const swipe = useSwipeGesture({
    threshold: 60,
    onSwipeLeft: () => {
      if (currentIndex < filtered.length - 1) setCurrentIndex((p) => p + 1);
    },
    onSwipeRight: () => {
      if (currentIndex > 0) setCurrentIndex((p) => p - 1);
    },
  });

  const handlePlay = (station: RadioStation) => {
    play(station);
  };

  return (
    <div className="flex flex-col h-full bg-background-50">
      {/* Header */}
      <div className="safe-top px-4 pt-4 pb-3">
        <h1 className="text-2xl font-bold text-foreground-950">Dial</h1>
        <p className="text-foreground-500 text-sm mt-0.5">Descubre radios online</p>
      </div>

      {/* Search */}
      <div className="px-4 pb-3">
        <div className="relative">
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar frecuencia, nombre o ciudad..."
            className="w-full pl-10 pr-4 py-3 bg-background-100 border border-background-200 rounded-xl text-foreground-950 text-sm placeholder-foreground-400 outline-none focus:border-primary-400 focus:bg-background-50 transition-all"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-foreground-300 flex items-center justify-center cursor-pointer"
            >
              <i className="ri-close-line text-background-50 text-xs"></i>
            </button>
          )}
        </div>
      </div>

      {/* Categories */}
      <div className="px-4 pb-4 overflow-x-auto scrollbar-none">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveCategory('')}
            className={`px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
              !activeCategory
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-100 text-foreground-600 hover:bg-background-200'
            }`}
          >
            Todas
          </button>
          {categoriesWithIcons.map((cat) => (
            <button
              key={cat.name}
              onClick={() => setActiveCategory(cat.name)}
              className={`px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer flex items-center gap-1.5 ${
                activeCategory === cat.name
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <i className={`${cat.icon} text-xs`}></i>
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Stations Grid */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 pb-24 scrollbar-none"
        {...swipe}
      >
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 rounded-full bg-accent-500/10 border border-accent-500/20 flex items-center justify-center mb-4">
              <i className="ri-signal-wifi-line text-2xl text-accent-400"></i>
            </div>
            <p className="text-foreground-800 font-semibold text-sm mb-1">Frecuencia disponible</p>
            <p className="text-foreground-500 text-xs text-center max-w-[240px]">
              Esta Frecuencia RFW aún está disponible. Mientras tanto, escuchá RFW Radio.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {filtered.map((station, idx) => {
              const isActive = currentStation?.id === station.id;
              return (
                <div
                  key={station.id}
                  className={`relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-200 active:scale-[0.97] ${
                    isActive && isPlaying
                      ? 'ring-2 ring-accent-500'
                      : 'hover:bg-background-100/80'
                  }`}
                  style={{
                    animation: `fadeInUp 0.4s ease-out forwards`,
                    animationDelay: `${idx * 50}ms`,
                    opacity: 0,
                  }}
                >
                  {/* Image */}
                  <div className="aspect-square w-full bg-background-200 overflow-hidden">
                    <img
                      src={station.logoUrl}
                      alt={station.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {/* Live badge */}
                    {station.isLive && (
                      <div className="absolute top-2 left-2 flex items-center gap-1 bg-accent-500/90 backdrop-blur-sm px-2 py-0.5 rounded-full">
                        <span className="w-1.5 h-1.5 bg-background-50 rounded-full animate-pulse"></span>
                        <span className="text-background-50 text-[10px] font-bold">EN VIVO</span>
                      </div>
                    )}
                    {/* Play overlay */}
                    <button
                      onClick={() => handlePlay(station)}
                      className="absolute inset-0 bg-foreground-950/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center"
                    >
                      <div className="w-12 h-12 rounded-full bg-primary-500/90 flex items-center justify-center">
                        <i className={`text-background-50 text-xl ${isActive && isPlaying ? 'ri-pause-fill' : 'ri-play-fill'}`}></i>
                      </div>
                    </button>
                  </div>

                  {/* Info */}
                  <div className="p-2.5">
                    <h3 className="text-foreground-950 font-semibold text-sm leading-tight truncate">
                      {station.name}
                    </h3>
                    <p className="text-foreground-500 text-xs mt-0.5 flex items-center gap-1">
                      <span>{station.frequency}</span>
                      <span className="text-foreground-300">·</span>
                      <span>{station.city.split(',')[0]}</span>
                    </p>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <span className="px-2 py-0.5 bg-background-100 rounded-md text-[10px] font-medium text-foreground-600">
                        {station.category}
                      </span>
                      <span className="text-[10px] text-foreground-400">
                        {(station.listeners / 1000).toFixed(1)}K
                      </span>
                    </div>
                  </div>

                  {/* Tap to enter */}
                  <Link
                    to={`/radio/${station.slug}`}
                    className="absolute bottom-2 right-2 w-7 h-7 rounded-full bg-background-50/90 backdrop-blur-sm flex items-center justify-center cursor-pointer shadow-sm hover:scale-110 transition-transform"
                  >
                    <i className="ri-arrow-right-line text-foreground-700 text-xs"></i>
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}